import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { ErrorBoundary } from '../../design-system/components/ErrorBoundary';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { useReadiness, useRobots } from '../../api/hooks';
import { useBuildingMap, useBuildingMapLevels } from '../../api/hooks/useBuildingMap';
import { useLifts } from '../../api/hooks/useLifts';
import { doorsRegistryQueryOptions, liftsRegistryQueryOptions } from '../../api/query-options';
import { recordedPosesOf } from '../../api/hooks/useRobotLastPoses';
import { robotReceiptKey } from '../../api/robot-receipts';
import type { SiteId } from '../../api/v1';
import { fetchRobotLastPoses } from '../../api/v1-client';
import { AuthContext } from '../../auth/AuthContext';
import { audienceFor } from '../../auth/roles';
import { getOptionalRuntimeConfig } from '../../config/runtime-config';
import { BuildingMapCanvas } from '../Map/BuildingMapCanvas';
// Selection state, the three detail drawers and the zone-click dispatch modal
// all live in the host, shared with the 3D view, so the two views cannot
// drift into being two command paths. See MapSelectionHost's own docstring.
import {
  MapSelectionHost,
  type MapSelectionHandlers,
  type MapSelectionState,
} from '../Map/MapSelectionHost';
import { useRobotPresence } from '../Map/useRobotPresence';
import { useLiftCarViews } from '../Devices/useLiftColumnState';
// A TYPE import: the value would pull three.js into the main bundle.
import type { GraphicsState } from '../Twin/twin-gl';
import { twinAssetFor } from '../Twin/twin-assets';
import { monotonicNow } from '../../state/clock';
import { liftLandingLevel } from '../../state/lift-landing';
import { formatFrom, locateHref, parseLocate, type LocateView } from '../../state/locate';
import type { LocateInventory } from '../../state/locate-resolve';
import { landingIdOf } from '../../state/show-where';
import { twinAvailability } from '../../state/twin-availability';
import type { ActiveSite } from '../../state/SiteContext';
import { useActiveSite } from '../../state/useActiveSite';
import { sameLevel } from '../../utils/levels';
import { naturalSort } from '../../utils/natural-sort';
import './OverviewScreen.css';
import { NoSiteNotice } from '../../shell/NoSiteNotice';
import { HelpScreenNote } from '../../help';
import { useCanSeeSimulated } from '../../auth/useCanSeeSimulated';
import { seedFloorFromRobots } from './default-floor';
import { LiveMapHeading } from './LiveMapHeading';
import { OverviewCallout } from './OverviewCallout';
import { MapBubble, MapHoverPeek } from './bubble/MapBubble';
import { useMapBubble } from './bubble/useMapBubble';
import type { MapAnchorSource, MapDevice } from '../Map/map-bubble-anchor';
import { OverviewFrame } from './OverviewFrame';
import { BUILDING_VIEW_FAILED_TEXT, type BuildingInventory } from './overview-building';
import {
  UNREADABLE_LINK,
  calloutModel,
  chooseHighlight,
  focusHighlight,
  landingClosesDrawer,
  landingIdFor,
  landingSubject,
  liftRobotFact,
  robotFloorFact,
  settledFloor,
  type CalloutOpen,
  type LandingFacts,
  type OverviewHighlight,
} from './overview-landing';
import { DEFAULT_OVERVIEW_VIEW, readStoredView, writeStoredView } from './overview-view';
import { OVERVIEW_WORDS } from './overview-words';
import { useOverviewLocate } from './useOverviewLocate';
import { useViewportWidth } from './useViewportWidth';

/**
 * The Building view, LAZILY: three.js and the renderer are fetched the first
 * time a person opens Building on this page, never on a page load. A static
 * import here would put ~135 kB gz of three.js on every session
 * (`bundle-budget.test.ts` refuses one).
 *
 * A FRESH ONE PER TRY. React keeps a rejected import for the life of its
 * `lazy` object, so after a load that failed -- a Wi-Fi blip, or a shell
 * served from cache after a deploy asking for a chunk that is gone -- the
 * person's retry (pressing Building again) needs a new one.
 */
const lazyBuildingView = () =>
  lazy(() => import('./OverviewBuildingView').then((m) => ({ default: m.OverviewBuildingView })));

/** What the boundary leaves where a Building view that could not load or
 *  draw was: nothing, and word to the Overview, which shows the Map. */
function BuildingViewFailed({ onFailed }: { onFailed: () => void }) {
  useEffect(() => {
    onFailed();
  }, [onFailed]);
  return null;
}

/**
 * How long the default-floor latch waits for the robots query's first
 * payload before seeding from the level list alone.
 *
 * Robots arrive over SSE and, in practice, AFTER the level list — so a
 * latch that fired the moment levels loaded would read an empty robot
 * array every single time and silently degrade to "first level", the
 * exact behaviour it exists to replace. Waiting is therefore not an
 * optimisation, it is the whole mechanism. The timeout is the other
 * half: a site with a dead SSE feed must still get a map, so the latch
 * gives up rather than leaving the pane blank forever.
 */
const ROBOT_SEED_TIMEOUT_MS = 3000;

/**
 * The Overview: the live map of the site, in 2D or 3D, and the place every
 * locate link lands (twin slice 1, plan W4 step 10; catalogue §8).
 *
 *   /overview?site=<site>[&view=2d|3d][&level=<L>][&focus=<kind>:<id>][&from=<origin>]
 *
 * A link POINTS. It lands once -- the floor, the ring, a callout saying
 * what was found -- and never opens a drawer, never carries a camera pose,
 * and never moves anything afterwards: the camera moves only for the
 * person -- a floor change, or the Building view's camera buttons. Moves
 * inside the view (the Map | Building switch, the floor picker and the
 * Building view's floor list, the callout's ×) REPLACE the URL and never
 * count as a landing. The rules are in `overview-landing.ts`.
 */
export function OverviewScreen() {
  const readiness = useReadiness();
  const { site } = useActiveSite();

  if (!site) {
    return (
      <div className="nx-overview">
        <h1 className="nx-overview__title">Operations Overview</h1>
        <NoSiteNotice />
      </div>
    );
  }

  return (
    <div className="nx-overview">
      <h1 className="nx-overview__title">Operations Overview</h1>
      <HelpScreenNote screen="overview" />

      {/* Hotel words (P9, 2026-10-31). The shell's banner above says
          the system is not ready, on every screen; this one says only
          what that does here, without repeating it: a zone on the map
          takes no new task until it is back (`overview.write_paused`). */}
      {readiness.isError && (
        <AlertBanner variant="error" title="Can't check the system right now">
          Showing the last known state. Live data comes back by itself when the connection does.
        </AlertBanner>
      )}
      {readiness.data?.status === 'degraded' && (
        <AlertBanner variant="warning" title="New tasks are paused">
          Zones on the map won&apos;t take a new task until the system is back. This clears by
          itself.
        </AlertBanner>
      )}

      {/* THE HOST, ABOVE EVERY LOADING BRANCH (plan W4 step 10). It used
          to sit inside the "map has loaded" branch, so a floor change --
          a new map query, briefly loading -- unmounted it and closed
          whatever drawer was open. Above them, a drawer survives a floor
          change and a Map | Building switch, and both views reach the
          same drawers through the same four callbacks. */}
      <MapSelectionHost>
        {(handlers, selection) => (
          <OverviewLive site={site} handlers={handlers} selection={selection} />
        )}
      </MapSelectionHost>
    </div>
  );
}

/** A landing (`overview-landing.ts`): what one link asked for, kept from
 *  the moment it arrived until the floor it lands on is known. */
interface Landing {
  readonly id: string;
  /** The site the link is for: its `site=`, or the one on screen. */
  readonly site: string;
  /** The `focus=` the link carried, as written. */
  readonly focusRaw: string | null;
  /** Arrived by a PUSH in this app, so Back returns to where it was made. */
  readonly pushed: boolean;
  readonly hasFrom: boolean;
  /** Monotonic ms the landing began: the start of "Finding X…". */
  readonly startedAtMs: number;
  /** The link's site has been checked (and switched to, if it can be). */
  readonly siteChecked: boolean;
  /** Its floor and drawer have been decided. Nothing re-lands after. */
  readonly settled: boolean;
}

/** The person's (or a landing's) floor: a level, and whether the Building
 *  view shows the whole building. Kept per site. */
interface FloorChoice {
  readonly site: string;
  readonly level: string | null;
  readonly whole: boolean;
}

/** The highlight as one stable object per `kind:id`: the 3D view rebuilds
 *  its live layer when the object changes. */
function useStableHighlight(h: OverviewHighlight | null): OverviewHighlight | null {
  const key = h ? `${h.kind}\u0000${h.id}` : '';
  const kept = useRef<{ key: string; h: OverviewHighlight | null }>({ key: '', h: null });
  if (kept.current.key !== key) kept.current = { key, h };
  return kept.current.h;
}

function OverviewLive({
  site,
  handlers,
  selection,
}: {
  site: ActiveSite;
  handlers: MapSelectionHandlers;
  selection: MapSelectionState;
}) {
  const siteId = site.id;
  const { sites, setSite } = useActiveSite();
  const roles = useContext(AuthContext)?.roles ?? null;
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const parsed = useMemo(() => parseLocate(location.search), [location.search]);
  // A link's focus, level and origin apply only to the site they name.
  const urlApplies = !parsed.site || parsed.site === siteId;

  const levelsQuery = useBuildingMapLevels();
  // The api-server derives this list by scanning Zenoh keys and
  // returning `sorted(levels)` — a lexicographic sort, under which
  // "L10" < "L4". Order it the way the building is stacked before it
  // reaches either the picker or the fallback below; sg-nomad-01
  // otherwise reads L10, L11 … L17, L4, L4M, L6 … L9.
  const levels = useMemo(() => naturalSort(levelsQuery.data ?? []), [levelsQuery.data]);
  const robots = useRobots();
  const liftsQuery = useLifts();
  // One presence for the map, the resolver and the ring.
  const presence = useRobotPresence(robots.data, liftsQuery.data);
  const liftRegistry = useQuery(liftsRegistryQueryOptions(siteId));
  const doorRegistry = useQuery(doorsRegistryQueryOptions(siteId));

  // ---- Default floor: open where a robot actually is, ONCE ----
  //
  // The floor to open on is "wherever there is something to watch", and
  // the cheapest true answer to that is a robot's own reported floor.
  // Strictly one shot per site: `robots.data` is fed by the live SSE
  // stream and changes continuously, so a seed that followed it would
  // re-pick the floor every time a robot rode a lift. The latch records
  // WHICH site it seeded, so switching sites reseeds and nothing else
  // does. A floor the person (or a landing) chose always wins over it,
  // and while a landing is still finding its floor the seed is not shown
  // at all: the latch is pre-empted by the link.
  const [seed, setSeed] = useState<{ site: string; level: string | null } | null>(null);
  // Only a robot that really said where it is seeds the floor: not a
  // stub, not the (map, 0, 0) sentinel, and not the simulated fleet for
  // a site audience (`seedFloorFromRobots`).
  const showSimulated = useCanSeeSimulated();
  const [robotWaitExpired, setRobotWaitExpired] = useState(false);
  useEffect(() => {
    setRobotWaitExpired(false);
    const id = window.setTimeout(() => setRobotWaitExpired(true), ROBOT_SEED_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [siteId]);
  // "The query has produced its first payload." `data !== undefined` is
  // the load-bearing half — TanStack leaves `data` undefined until a
  // fetch succeeds — with `isSuccess` as the explicit signal and
  // `isError`/the timeout as the escapes, so a site whose robot feed
  // never answers still gets a floor.
  const robotsSettled =
    robots.isSuccess === true ||
    robots.data !== undefined ||
    robots.isError === true ||
    robotWaitExpired;
  // Where a robot was last seen (2026-10-06), for when none says where it
  // is now: one read per site, as far back as the site keeps poses
  // (`since_ms=0` is clamped to its retention), never refetched -- it
  // only ever chooses the opening floor. Its own key, so the twin's
  // 24 h ghost query is not widened by it.
  const lastSeen = useQuery({
    queryKey: ['robot-last-poses-seed', siteId] as const,
    queryFn: () => fetchRobotLastPoses(siteId as SiteId, 0),
    enabled: Boolean(siteId),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const lastSeenSettled =
    lastSeen.data !== undefined || lastSeen.isError === true || robotWaitExpired;
  useEffect(() => {
    if (levels.length === 0 || seed?.site === siteId || !robotsSettled) return;
    // A robot that says where it is now wins at once; otherwise the floor
    // it was last seen on, once the recorder has answered (or the wait
    // ran out); only with neither, the natural-sorted first level, i.e.
    // the bottom of the building.
    const recorded = lastSeenSettled ? recordedPosesOf(lastSeen.data) : undefined;
    const onFloor = seedFloorFromRobots(robots.data, levels, showSimulated, recorded);
    if (onFloor === null && !lastSeenSettled) return;
    setSeed({ site: siteId, level: onFloor ?? levels[0] ?? null });
  }, [levels, siteId, robotsSettled, robots.data, showSimulated, seed, lastSeenSettled, lastSeen.data]);

  // ---- The view: Map or Building ----
  //
  // A link's `view=` is honoured and never remembered; otherwise this
  // device's own last choice, and the Map where it has none (Decision 7).
  const [view, setView] = useState<LocateView>(
    () => parseLocate(location.search).view ?? readStoredView() ?? DEFAULT_OVERVIEW_VIEW,
  );

  // ---- Landings ----
  //
  // The last landing id handled lives in a REF and is compared by value:
  // react-router mints a new `location.key` on every replace, and every
  // move inside this view is a replace, so a landing keyed on the key
  // would re-land on each one (`landingIdFor`).
  const handledRef = useRef<string | null>(null);
  const firstLookRef = useRef(true);
  const [landing, setLanding] = useState<Landing | null>(null);
  const [chosen, setChosen] = useState<FloorChoice | null>(null);
  const [siteNotice, setSiteNotice] = useState<string | null>(null);
  useLayoutEffect(() => {
    const id = landingIdFor({
      firstLook: firstLookRef.current,
      navigationType,
      search: location.search,
      state: location.state,
    });
    firstLookRef.current = false;
    if (id === null || id === handledRef.current) return;
    handledRef.current = id;
    const p = parseLocate(location.search);
    setLanding({
      id,
      site: p.site ?? siteId,
      focusRaw: p.focusRaw,
      pushed: navigationType === 'PUSH' && landingIdOf(location.state) !== null,
      hasFrom: p.from !== null,
      startedAtMs: monotonicNow(),
      siteChecked: false,
      settled: false,
    });
    setSiteNotice(null);
    // Honoured for this landing, and never written to the device's memory.
    if (p.view) setView(p.view);
    // `siteId` is read for a link that names no site: it is the one on
    // screen when the link arrived, and a later switch is not a new link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state, navigationType]);

  // The link's site: the one on screen, one this login may switch to (and
  // does, saying so), or one it may not see -- said, and nothing of it is
  // shown. Another site's building is never drawn.
  useEffect(() => {
    if (!landing || landing.siteChecked) return;
    const mark = (over: Partial<Landing>) =>
      setLanding((l) => (l && l.id === landing.id ? { ...l, siteChecked: true, ...over } : l));
    if (landing.site === siteId) {
      mark({});
      return;
    }
    const target = sites.find((s) => s.site === landing.site);
    if (target) {
      setSiteNotice(OVERVIEW_WORDS.switchedSite(target.label));
      mark({});
      setSite(target.site);
      return;
    }
    if (sites.length > 0) {
      setSiteNotice(OVERVIEW_WORDS.noSiteAccess);
      mark({ settled: true });
    }
  }, [landing, siteId, sites, setSite]);

  // ---- The focus on screen ----
  const focusRaw = urlApplies ? parsed.focusRaw : null;
  const focus = urlApplies ? parsed.focus : null;
  // When this focus appeared: the landing's own start for its focus, else
  // the first render that saw it (a Back to an older link).
  const seenRef = useRef<{ raw: string | null; at: number }>({ raw: null, at: 0 });
  if (seenRef.current.raw !== focusRaw) seenRef.current = { raw: focusRaw, at: monotonicNow() };
  const startedAtMs =
    landing && landing.site === siteId && landing.focusRaw === focusRaw
      ? landing.startedAtMs
      : seenRef.current.at;

  // ---- Can the Building view be shown here? ----
  const width = useViewportWidth();
  const [graphics, setGraphics] = useState<{ site: string; state: GraphicsState } | null>(null);
  const onGraphicsState = useCallback(
    (state: GraphicsState) => setGraphics({ site: siteId, state }),
    [siteId],
  );
  const availabilityInputs = {
    twinEnabled: getOptionalRuntimeConfig()?.featureFlags.twinEnabled,
    roles,
    hasAsset: twinAssetFor(siteId) !== null,
  };
  const availability = twinAvailability({
    ...availabilityInputs,
    screenWidthPx: width,
    graphics: graphics?.site === siteId ? graphics.state : null,
  });
  // The switch is offered where the building view exists for this login
  // and site at all; a screen or a device that cannot draw it disables
  // its Building half, and says why when asked for it.
  const buildingOffered = twinAvailability({
    ...availabilityInputs,
    screenWidthPx: null,
    graphics: null,
  }).available;
  // A Building view that could not load (or threw while drawing) is
  // unavailable for this page until the person asks for it again.
  const [OverviewBuildingView, setOverviewBuildingView] = useState(lazyBuildingView);
  const [buildingFailed, setBuildingFailed] = useState(false);
  const onBuildingFailed = useCallback(() => setBuildingFailed(true), []);
  const shows3d = view === '3d' && availability.available && !buildingFailed;
  const fallbackText =
    view !== '3d'
      ? null
      : !availability.available
        ? availability.text
        : buildingFailed
          ? BUILDING_VIEW_FAILED_TEXT
          : null;
  const buildingBlocked =
    !availability.available &&
    (availability.reason === 'narrow' || availability.reason === 'no-webgl');

  // ---- What the view on screen draws, for "Not drawn ..." ----
  const [buildingInventory, setBuildingInventory] = useState<{
    site: string;
    inventory: BuildingInventory;
  } | null>(null);
  const onInventory = useCallback(
    (inventory: BuildingInventory) => setBuildingInventory({ site: siteId, inventory }),
    [siteId],
  );
  const inventory = useMemo<LocateInventory | null>(() => {
    if (shows3d) {
      return buildingInventory?.site === siteId
        ? { view: '3d', ...buildingInventory.inventory }
        : null;
    }
    // The Map draws the lift registry's cars on every floor they serve and
    // every registry door on its own floor.
    if (!liftRegistry.data || !doorRegistry.data) return null;
    return {
      view: '2d',
      lifts: new Set(liftRegistry.data.map((r) => r.id.lift)),
      doors: new Set(doorRegistry.data.map((r) => r.id.door)),
    };
  }, [shows3d, buildingInventory, siteId, liftRegistry.data, doorRegistry.data]);

  const resolution = useOverviewLocate({
    siteId,
    focus,
    levelHint: urlApplies ? parsed.level : null,
    startedAtMs,
    levels: levelsQuery.data ? levels : undefined,
    robots: robots.data,
    lifts: liftsQuery.data,
    presence,
    inventory,
  });

  // Each car's look: the one reading the map draws and the callout words.
  const liftNames = useMemo(() => {
    const names = new Set<string>();
    for (const l of liftsQuery.data ?? []) names.add(l.name);
    for (const r of liftRegistry.data ?? []) names.add(r.id.lift);
    return [...names];
  }, [liftsQuery.data, liftRegistry.data]);
  const carViews = useLiftCarViews(liftNames, liftsQuery.data);
  const facts = useMemo<LandingFacts>(
    () => ({
      carLevel: (name) => {
        const v = carViews.get(name);
        return v && v.look !== 'hollow' ? v.level : null;
      },
      liftRobot: (name) => {
        const lift = liftsQuery.data?.find((l) => l.name === name);
        return lift ? liftRobotFact(liftLandingLevel(lift, robots.data, presence), presence) : null;
      },
      robotFloor: (fleet, name) => robotFloorFact(presence.get(robotReceiptKey(fleet, name))),
    }),
    [carViews, liftsQuery.data, robots.data, presence],
  );

  // ---- The floor ----
  const chosenHere = chosen?.site === siteId ? chosen : null;
  const seedHere = seed?.site === siteId ? seed : null;
  const pending = !!landing && !landing.settled && landing.site === siteId;
  // While a landing is finding its floor: the floor already on screen if
  // the person had one, else the link's level hint if the site has it,
  // else nothing yet -- never the seed, which the link pre-empts.
  const provisional = pending && resolution?.status === 'finding' ? resolution.level : null;
  const decided: { level: string | null; whole: boolean } | null = chosenHere
    ? { level: chosenHere.level, whole: chosenHere.whole }
    : pending
      ? provisional
        ? { level: provisional, whole: false }
        : null
      : seedHere
        ? { level: seedHere.level, whole: false }
        : null;
  const inLevels = (l: string | null | undefined) =>
    l ? (levels.find((x) => sameLevel(x, l)) ?? null) : null;
  // The Map always shows a floor: the decided one where the site has it,
  // else the seed's, else the bottom of the building.
  const mapLevel = decided
    ? (inLevels(decided.level) ??
      inLevels(seedHere?.level) ??
      (seedHere ? (levels[0] ?? null) : null))
    : null;
  const buildingLevel = decided && !decided.whole ? decided.level : null;
  const buildingReady =
    decided !== null || (levelsQuery.isFetched === true && levels.length === 0 && !pending);

  // The landing settles ONCE: the floor it lands on, and the open drawer
  // closed if it is about something else. After this nothing the live
  // data does -- a robot riding to another floor, a new answer -- moves
  // the floor, and so nothing moves the camera.
  useEffect(() => {
    if (!landing || landing.settled || !landing.siteChecked || landing.site !== siteId) return;
    let floor: { level: string | null } | null;
    if (!focusRaw) {
      // A link to no object: its level, if it named one.
      floor = parsed.level && urlApplies ? { level: inLevels(parsed.level) ?? parsed.level } : null;
    } else if (!focus || !resolution) {
      floor = null;
    } else {
      floor = settledFloor(resolution, facts);
      if (!floor) return;
    }
    if (floor?.level) setChosen({ site: siteId, level: floor.level, whole: false });
    else if (floor) {
      // The whole building: the Map keeps the floor it had.
      setChosen((c) => ({ site: siteId, level: c?.site === siteId ? c.level : null, whole: true }));
    }
    if (landingClosesDrawer(selection.selected, landingSubject(focus, resolution))) {
      selection.clear();
    }
    setLanding((l) => (l && l.id === landing.id ? { ...l, settled: true } : l));
    // `inLevels` reads `levels`, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    landing,
    siteId,
    focus,
    focusRaw,
    resolution,
    facts,
    selection,
    parsed.level,
    urlApplies,
    levels,
  ]);

  const mapQuery = useBuildingMap(shows3d ? null : mapLevel);
  /**
   * "There is a map coming, it just isn't here yet."
   *
   * `useBuildingMap(null)` is a DISABLED query, and a disabled query
   * never reports `isLoading` — so during the latch's own wait (levels
   * known, first robot payload not yet in) the pane would have no floor,
   * no data, and nothing claiming to be loading, and render "No building
   * map available". The wait is bounded by `ROBOT_SEED_TIMEOUT_MS` (and a
   * landing's by its 5 s), so this skeleton is too. The level list's own
   * fetch counts for the same reason.
   */
  const mapPending =
    mapQuery.isLoading === true ||
    levelsQuery.isLoading === true ||
    (mapLevel === null && levels.length > 0);

  // ---- The tap bubble ----
  const bubble = useMapBubble(handlers, selection);
  const anchorSourceRef = useRef<MapAnchorSource | null>(null);
  const mapFrameRef = useRef<HTMLDivElement | null>(null);
  // The hover peek's words change when the device under the mouse does; where
  // it sits is set by hand, so crossing the map renders nothing.
  const [hovered, setHovered] = useState<MapDevice | null>(null);
  const peekRef = useRef<HTMLDivElement | null>(null);
  const onHoverDevice = useCallback((device: MapDevice | null, x: number, y: number) => {
    setHovered((cur) =>
      cur?.kind === device?.kind && cur?.id === device?.id ? cur : device,
    );
    if (peekRef.current) {
      peekRef.current.style.transform = `translate(${x + 14}px, ${y + 14}px)`;
    }
  }, []);

  // ---- The ring ----
  const focusSubject = landingSubject(focus, resolution);
  // The device with the bubble keeps a ring, as one with its panel open does.
  const ringSubject = selection.selected ?? bubble.device;
  const ringKind = ringSubject?.kind;
  const ringId = ringSubject?.id;
  const selectedRing = useMemo<OverviewHighlight | null>(() => {
    const sel = ringKind && ringId !== undefined ? { kind: ringKind, id: ringId } : null;
    if (!sel) return null;
    if (sel.kind !== 'robot') return { kind: sel.kind, id: sel.id };
    // The host keys a robot by its bare name; a ring is fleet-qualified,
    // and a name two fleets share is not guessed.
    const matches = (robots.data ?? []).filter((r) => r.name === sel.id);
    if (matches.length !== 1) return null;
    const key = robotReceiptKey(matches[0]!.fleet, matches[0]!.name);
    const p = presence.get(key);
    return p?.kind === 'riding' && p.liftName
      ? { kind: 'lift', id: p.liftName }
      : { kind: 'robot', id: key };
  }, [ringKind, ringId, robots.data, presence]);
  const highlight = useStableHighlight(
    chooseHighlight({
      focusRing: focusHighlight(resolution),
      focusSubject,
      selected: ringSubject,
      selectedRing,
    }),
  );

  // ---- In-view moves: a replace, never a landing ----
  const inViewHref = (over: { view?: LocateView; level?: string | null; dropFocus?: boolean }) =>
    locateHref({
      site: siteId,
      view: over.view ?? parsed.view,
      level: over.level !== undefined ? over.level : urlApplies ? parsed.level : null,
      focus: over.dropFocus || !urlApplies ? null : parsed.focusRaw,
      from: over.dropFocus || !urlApplies || !parsed.from ? null : formatFrom(parsed.from),
    });
  const replaceWith = (href: string) => navigate(href, { replace: true, state: location.state });

  const switchView = (next: LocateView) => {
    // The person's own choice: remembered on this device.
    writeStoredView(next);
    setView(next);
    // Asking for Building again is the person's retry after a stuck view,
    // and after one that could not load: with a fresh lazy import.
    if (next === '3d') {
      setGraphics(null);
      if (buildingFailed) {
        setBuildingFailed(false);
        setOverviewBuildingView(() => lazyBuildingView());
      }
    }
    replaceWith(inViewHref({ view: next }));
  };
  const chooseFloor = (value: string) => {
    const whole = value === '';
    setChosen((c) => ({
      site: siteId,
      level: whole ? (c?.site === siteId ? c.level : mapLevel) : value,
      whole,
    }));
    replaceWith(inViewHref({ level: whole ? null : value }));
  };
  // The Building view's own floor list, a floor's plate and its "Whole
  // building" (null): the person's choice, made as the Map's picker makes
  // it, in the site's spelling where the site has the floor.
  const chooseBuildingFloor = (choice: string | null) =>
    chooseFloor(choice === null ? '' : (inLevels(choice) ?? choice));
  const closeCallout = () => replaceWith(inViewHref({ dropFocus: true }));
  const openPanel = (open: CalloutOpen) => {
    if (open.kind === 'robot') handlers.onSelectRobot(open.id);
    else if (open.kind === 'lift') handlers.onSelectLift(open.id);
    else handlers.onSelectDoor(open.id);
  };

  const viewFloor = shows3d ? buildingLevel : mapLevel;
  const callout = focus
    ? resolution && calloutModel({ focus, resolution, viewFloor, facts })
    : focusRaw
      ? UNREADABLE_LINK
      : null;
  const showBack = !!landing && landing.pushed && landing.hasFrom;

  // The Map's floor picker. The Building view has its own floor list
  // (`FloorList`, plan W2 step 12), with "Whole building" beside the
  // drawing, so it shows none.
  const showFloorPicker = !shows3d && levels.length > 1;

  return (
    <OverviewFrame view={view} onOpenPanel={openPanel}>
      <section className="nx-overview__section nx-overview__section--hero">
        <div className="nx-map-screen__head">
          {/* The dot follows FeedHealth, not a timer: it pulses only while
              the feed is live, and the heading says why when it is not. */}
          <LiveMapHeading siteId={siteId} label={site.label} />
          <div className="nx-overview__controls">
            {showBack && (
              <button
                type="button"
                className="nx-overview__back"
                onClick={() => navigate(-1)}
                data-testid="overview-back"
              >
                <span aria-hidden="true">← </span>
                {OVERVIEW_WORDS.back}
              </button>
            )}
            {buildingOffered && (
              <div
                className="nx-overview-switch"
                role="group"
                aria-label={OVERVIEW_WORDS.viewGroup}
                data-testid="overview-view-switch"
              >
                <button
                  type="button"
                  aria-pressed={!shows3d}
                  onClick={() => switchView('2d')}
                  data-testid="overview-view-map"
                >
                  {OVERVIEW_WORDS.map}
                </button>
                <button
                  type="button"
                  aria-pressed={shows3d}
                  disabled={buildingBlocked}
                  onClick={() => switchView('3d')}
                  data-testid="overview-view-building"
                >
                  {OVERVIEW_WORDS.building}
                </button>
              </div>
            )}
            {showFloorPicker && (
              <label className="nx-map-screen__level-picker">
                {OVERVIEW_WORDS.floor}
                <select
                  value={mapLevel ?? ''}
                  onChange={(e) => chooseFloor(e.target.value)}
                  data-testid="overview-map-level-select"
                >
                  {levels.map((lv) => (
                    <option key={lv} value={lv}>
                      {lv}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
        {siteNotice && (
          <p className="nx-overview__notice" role="status" data-testid="overview-site-notice">
            {siteNotice}
          </p>
        )}
        {fallbackText && (
          <p className="nx-overview__notice" role="status" data-testid="overview-view-fallback">
            {fallbackText}
          </p>
        )}
        {/* Plain words for everyone; the error's own text (an HTTP status,
            a fetch message) is ops' only, collapsed (copy deck v2, N7). */}
        {!shows3d && mapQuery.isError && (
          <AlertBanner variant="error" title={OVERVIEW_WORDS.mapLoadErrorTitle}>
            <span data-testid="overview-map-error">
              {OVERVIEW_WORDS.mapLoadErrorText}
              {mapQuery.data ? ` ${OVERVIEW_WORDS.mapLoadErrorStale}` : ''}
            </span>
            {audienceFor(roles) === 'ops' && (
              <details className="nx-overview__error-details" data-testid="overview-map-error-details">
                <summary>{OVERVIEW_WORDS.details}</summary>
                {String((mapQuery.error as Error)?.message ?? 'Unknown error')}
              </details>
            )}
          </AlertBanner>
        )}
        {callout && <OverviewCallout model={callout} onClose={closeCallout} onOpen={openPanel} />}
        <div ref={mapFrameRef} className="nx-overview__map-wrap" data-view={shows3d ? '3d' : '2d'}>
          {shows3d ? (
            buildingReady ? (
              // Its own boundary: a chunk that will not load must not take
              // the Map, the callout and an open drawer down with it (the
              // route's boundary would show "Something went wrong").
              <ErrorBoundary fallback={() => <BuildingViewFailed onFailed={onBuildingFailed} />}>
                <Suspense fallback={<SkeletonLoader shape="card-block" count={1} />}>
                  <OverviewBuildingView
                    siteId={siteId}
                    level={buildingLevel}
                    highlight={highlight}
                    onGraphicsState={onGraphicsState}
                    onInventory={onInventory}
                    onChooseFloor={chooseBuildingFloor}
                    {...bubble.tap}
                    anchorSourceRef={anchorSourceRef}
                    onTapNothing={bubble.close}
                    onHoverDevice={onHoverDevice}
                  />
                </Suspense>
              </ErrorBoundary>
            ) : (
              <SkeletonLoader shape="card-block" count={1} />
            )
          ) : mapPending ? (
            <SkeletonLoader shape="card-block" count={1} />
          ) : mapQuery.data && mapQuery.data.levels && mapQuery.data.levels.length > 0 ? (
            <BuildingMapCanvas
              buildingMap={mapQuery.data}
              {...bubble.tap}
              anchorSourceRef={anchorSourceRef}
              onTapNothing={bubble.close}
              onHoverDevice={onHoverDevice}
              robotPresence={presence}
              liftLooks={carViews}
              highlight={highlight}
            />
          ) : (
            // Reserved for the genuine case — the map query has
            // settled (or there is no floor to ask about) and there is
            // still nothing to draw. Never the seeding window above.
            <p className="nx-overview__empty" data-testid="overview-map-empty">
              No building map available for {site.label}.
            </p>
          )}
          {bubble.device && (
            <MapBubble
              key={`${bubble.device.kind}:${bubble.device.id}`}
              device={bubble.device}
              anchorSourceRef={anchorSourceRef}
              frameRef={mapFrameRef}
              presenceMap={presence}
              onClose={bubble.close}
              onDetails={bubble.openPanel}
              onShowLevel={(floor) => (shows3d ? chooseBuildingFloor(floor) : chooseFloor(floor))}
            />
          )}
          {/* The bubble replaces the peek for what it is about. */}
          <MapHoverPeek
            device={hovered && !(bubble.device && bubble.device.kind === hovered.kind && bubble.device.id === hovered.id) ? hovered : null}
            peekRef={peekRef}
            presenceMap={presence}
          />
        </div>
      </section>
    </OverviewFrame>
  );
}
